import React from 'react';
import { Navbar } from '../components/landing/Navbar';
import { HeroSection } from '../components/landing/HeroSection';
import PricingSection from '../components/landing/PricingSection';
import { FeaturesSection } from '../components/landing/FeaturesSection';
import ScreenshotShowcase from '../components/landing/ScreenshotShowcase'; // Changed to default import
import { Footer } from '../components/landing/Footer';
import TestimonialSection from '../components/landing/TestimonialSection';
import { ResourcesSection } from '../components/landing/ResourcesSection';
import { CtaSection } from '../components/landing/CtaSection';
import { pricingPlans, aiQuotas } from '../config/pricing';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

const LandingPage: React.FC = () => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
<div data-page="landing" className="min-h-screen flex flex-col overflow-x-hidden">
        <Navbar />
      <HeroSection />
      <ScreenshotShowcase />
      <TestimonialSection />
      <PricingSection />
      <FeaturesSection />
      <ResourcesSection />
      <div id="faq" className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Frequently Asked Questions</h2>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqData.filter(faq => typeof faq.answer === 'string').map(faq => ({
                  "@type": "Question",
                  "name": faq.question,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                  }
                }))
              })
            }}
          />
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow p-4 cursor-pointer" onClick={() => toggleItem(index)}>
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold">{faq.question}</h3>
                  <ChevronDown className={`h-5 w-5 transition-transform ${openItems.includes(index) ? 'rotate-180' : ''}`} />
                </div>
                {openItems.includes(index) && (
                  <div className="mt-2 text-gray-600">{faq.answer}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <CtaSection />
      <Footer />
    </div>
  );
};

export default LandingPage;

interface FAQItem {
  question: string;
  answer: string | JSX.Element;
}

const faqData: FAQItem[] = [
  {
    question: "What is Simplified Trading Journal?",
    answer: "Simplified Trading Journal is an AI-powered trading journal application that helps traders track their trades, analyze performance, and improve their trading strategies through intelligent insights and comprehensive analytics."
  },
  {
    question: "How does the AI trading coach work?",
    answer: "Our AI trading coach analyzes your trading patterns, performance metrics, and trade history to provide personalized insights and recommendations. It helps identify strengths, weaknesses, and areas for improvement in your trading strategy."
  },
  {
    question: "What trading platforms can I import data from?",
    answer: "You can import trade data through CSV files from most major trading platforms including NinjaTrader and Tradovate, paste directly from TradingView, or manually enter trades. We support data from brokers like TD Ameritrade, Interactive Brokers, E*TRADE, and many others."
  },
  {
    question: "How much does Simplified Trading Journal cost?",
    answer: "We offer a 3-day free trial with no card needed, and then $14.99 monthly or $99.99 yearly with our current limited time offer that is 44% off when you take yearly which equates to $8.33 monthly."
  },
  {
    question: "Is there a mobile app available?",
    answer: "Coming soon."
  },
  {
    question: "What analytics and reports are available?",
    answer: "Our platform provides comprehensive analytics including P&L charts, equity curves, win/loss ratios, average trade performance, risk-reward ratios, daily/weekly/monthly performance summaries, and detailed trade statistics."
  },
  {
    question: "How secure is my trading data?",
    answer: "We take security seriously with enterprise-grade encryption, row-level security policies, and complete user data isolation. Your trading data is stored securely and is never shared with third parties."
  },
  {
    question: "Can I track multiple trading strategies?",
    answer: "Absolutely! Our Trading Models feature allows you to create, manage, and track performance across multiple trading strategies. You can assign trades to specific models and compare their performance."
  },
  {
    question: "What is the AI usage quota system?",
    answer: `Different subscription plans include varying levels of AI analysis: ${aiQuotas.trial.description}, ${aiQuotas.monthly.description}, and ${aiQuotas.yearly.description}.`
  },
  {
    question: "How do I cancel my subscription?",
    answer: "You can cancel your subscription at any time through your account settings or by contacting our support team. There are no cancellation fees, and you'll retain access until the end of your billing period."
  },
  {
    question: "Do you offer educational content for trading?",
    answer: "Yes! We provide trading education content, methodology explanations, risk management guidance, and best practices for using trading journals effectively to improve your trading performance."
  },
  {
    question: "Can I export my trading data?",
    answer: "Yes, you can export your trade data in various formats for external analysis, tax reporting, or backup purposes. We support CSV exports and detailed performance reports."
  },
  {
    question: "What makes Simplified Trading Journal different from competitors?",
    answer: "Our key differentiators include AI-powered insights, habit discipline tracking, affordable pricing, comprehensive import options, real-time analytics, and a focus on both performance tracking and trading education."
  },
  {
    question: "How does the free trial work?",
    answer: "Our 3-day free trial gives you complete access to all features without requiring a credit card. You can explore the full platform, import trades, use AI analysis, and experience all premium features."
  },
  {
    question: "Is customer support available?",
    answer: "Yes, we provide fast customer support through email. We aim to respond to all inquiries within 24 hours maximum, often much sooner."
  }
];